import { cur } from '@/lib/currency';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Building2, Coins, TrendingUp, TrendingDown } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

// Per-branch tile grid for the dashboard. Each tile carries the branch's
// month sales + net profit, the inter-branch owed/owe chip (click → ledger),
// and the per-branch "auto-net internal offsets nightly" switch.
export default function BranchTilesWidget({ branches = [] }) {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/branches/summary-batch');
      setSummaries(res.data?.summaries || {});
    } catch {
      setSummaries({});
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAutoOffset = async (branchId, next) => {
    setSavingId(branchId);
    // optimistic
    setSummaries(prev => ({ ...prev, [branchId]: { ...(prev[branchId] || {}), auto_offset_enabled: next } }));
    try {
      const r = await api.put(`/branches/${branchId}/auto-offset`, { enabled: next });
      const cleared = r.data?.cleared?.created || 0;
      toast.success(next
        ? (cleared ? `Auto-net ON — netted ${cleared} balance(s) now` : 'Auto-net ON')
        : 'Auto-net OFF');
      load();
    } catch (e) {
      setSummaries(prev => ({ ...prev, [branchId]: { ...(prev[branchId] || {}), auto_offset_enabled: !next } }));
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSavingId(null); }
  };

  const list = branches.filter(b => summaries[b.id]);
  if (loading) {
    return (
      <Card className="border-border" data-testid="branch-tiles-widget">
        <CardContent className="p-4 text-sm text-muted-foreground">Loading branch tiles…</CardContent>
      </Card>
    );
  }
  if (!list.length) return null;

  return (
    <Card className="border-border" data-testid="branch-tiles-widget">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Building2 size={16} className="text-indigo-500" />
          <h3 className="text-sm font-bold text-foreground">Branches</h3>
          <span className="text-[11px] text-muted-foreground">· this month · owed/owe + auto-net</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {list.map(b => {
            const s = summaries[b.id] || {};
            const net = s.net_profit || 0;
            const ib = s.inter_branch || {};
            const outstanding = ib.net_outstanding || 0;
            const owedToYou = outstanding >= 0;
            const showChip = Math.abs(outstanding) > 0.5;
            const auto = !!s.auto_offset_enabled;
            return (
              <div key={b.id} className="rounded-xl border border-border p-3 flex flex-col gap-2 bg-gradient-to-br from-muted/30 to-transparent" data-testid={`branch-tile-${b.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate" title={b.name}>{b.name}</p>
                  <div className="flex items-center gap-1.5 mt-1 text-[11px]">
                    <span className="text-success">{cur()} {(s.total_sales || 0).toFixed(0)}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className={net >= 0 ? 'text-success' : 'text-error'}>
                      {net >= 0 ? <TrendingUp size={10} className="inline mr-0.5" /> : <TrendingDown size={10} className="inline mr-0.5" />}
                      {cur()} {net.toFixed(0)}
                    </span>
                  </div>
                </div>

                {showChip ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/inter-branch-ledger?branch=${b.id}`)}
                    data-testid={`branch-tile-chip-${b.id}`}
                    title={owedToYou ? 'Other branches owe this branch — view ledger' : 'This branch owes other branches — view ledger'}
                    className={`inline-flex items-center self-start rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${owedToYou ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
                  >
                    <Coins size={10} className="mr-0.5" />
                    {owedToYou ? 'Owed to you ' : 'You owe '}{cur()} {Math.abs(outstanding).toFixed(0)}
                  </button>
                ) : (
                  <span className="text-[10px] text-muted-foreground self-start" data-testid={`branch-tile-settled-${b.id}`}>✓ Settled</span>
                )}

                <label className="flex items-center justify-between gap-2 mt-auto pt-1 border-t border-border/50 cursor-pointer">
                  <span className="text-[10px] text-muted-foreground">Auto-net nightly</span>
                  <Switch
                    checked={auto}
                    disabled={savingId === b.id}
                    onCheckedChange={(v) => toggleAutoOffset(b.id, v)}
                    data-testid={`branch-tile-auto-offset-${b.id}`}
                  />
                </label>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
